/**
 * THE PORTAL'S MAP — the one source for the rail's doors, each section's page
 * column, and "where am I" (modelled on nucleus's frontend/src/nucleus/map.js).
 *
 * A section is a door on the rail. A section with more than one page stands a
 * page column (the kit's TileColumn) beside the desk; a section with one page
 * goes straight to it. Home is the rail's mark, not a door.
 *
 * Every page points at a URL the portal already serves — the frame changed,
 * the addresses did not, so no link, bookmark or email anywhere breaks.
 */
import { Boxes, Plug, Shapes, Terminal, Coins, Activity, BookOpen } from 'lucide-react';

export const SECTIONS = [
  // Apps, Plugins and Schemas are three doors, as they were before the frame,
  // not one door with a column: inside an app the column was a third list
  // beside the app's own pages, and almost all of it empty.
  {
    id: 'apps', label: 'Apps', glyph: Boxes,
    pages: [{ id: 'apps', label: 'Apps', about: 'Everything you build, by stage', to: '/applications' }],
  },
  {
    id: 'plugins', label: 'Plugins', glyph: Plug,
    pages: [{ id: 'plugins', label: 'Plugins', about: 'Tools agents run on someone\'s data', to: '/plugins' }],
  },
  {
    id: 'schemas', label: 'Schemas', glyph: Shapes,
    pages: [{ id: 'schemas', label: 'Schemas', about: 'Node types your apps write', to: '/schemas' }],
  },
  {
    id: 'develop', label: 'Develop', glyph: Terminal,
    pages: [
      { id: 'new', label: 'New app', about: 'From nothing to a registered app', to: '/create?lane=new' },
      { id: 'continue', label: 'Continue', about: 'Pick up an app you own on this machine', to: '/create?lane=continue' },
      { id: 'tokens', label: 'Tokens', about: 'Credentials for the CLI and CI', to: '/tokens' },
    ],
  },
  {
    id: 'money', label: 'Money', glyph: Coins,
    pages: [
      { id: 'money', label: 'Money', about: 'Payouts, prices, earnings', to: '/billing' },
    ],
  },
  {
    id: 'usage', label: 'Usage', glyph: Activity,
    pages: [
      { id: 'metrics', label: 'Calls', about: 'Every call, across your apps', to: '/applications/metrics' },
      { id: 'evals', label: 'OS-Evals', about: 'The three scores, per app', to: '/applications/os-evals' },
      { id: 'connections', label: 'Connections', about: 'Edges, reach and pull', to: '/applications/connections' },
    ],
  },
  {
    id: 'learn', label: 'Learn', glyph: BookOpen,
    pages: [
      { id: 'docs', label: 'Docs', about: 'Quickstart and guides', to: '/documentation' },
      { id: 'cli', label: 'CLI', about: 'Every command, by what you are doing', to: '/cli' },
      { id: 'releases', label: 'Releases', about: 'What changed in the platform', to: '/releases' },
    ],
  },
];

export const sectionFor = (id) => SECTIONS.find((s) => s.id === id) || null;

/**
 * ONE APP'S PAGES, listed inside the app page itself (components/dev/
 * AppPageNav.js) in three small groups — plain names, each with a line saying
 * what it is for, under a way back to All apps. The ids
 * are the app page's `?panel=` values, unchanged, so every deep link lands.
 */
export const APP_PAGES = [
  { id: 'overview', group: 'Set up', label: 'Overview', about: 'Where it stands, its IDs and settings' },
  { id: 'access', group: 'Set up', label: 'Data it asks for', about: 'What people grant when they let it in' },
  { id: 'pricing', group: 'Set up', label: 'Price', about: 'What it costs, and getting paid' },
  { id: 'crux', group: 'Set up', label: 'Design doc', about: 'What it is for. The consent screen is built from it' },
  { id: 'keys', group: 'Build', label: 'API keys', about: 'One per machine that runs it' },
  { id: 'testusers', group: 'Build', label: 'Test users', about: 'Sandbox accounts that already granted it' },
  { id: 'graph', group: 'Build', label: 'Built-in data', about: 'Nodes it ships to everyone' },
  { id: 'api', group: 'Build', label: 'Its API', about: 'Endpoints it offers other apps' },
  { id: 'health', group: 'Results', label: 'Health', about: 'Calls, people, and who stayed' },
  { id: 'readiness', group: 'Results', label: 'OS-Evals', about: 'Three scores, and every check behind them' },
  { id: 'earnings', group: 'Results', label: 'Earnings', about: 'Who pays, and what has been collected' },
  { id: 'versions', group: 'Results', label: 'History', about: 'Every version, and rolling back' },
];

/** The address of one of an app's pages (Overview is the bare app address). */
export const appPagePath = (appId, pageId) => (
  !pageId || pageId === 'overview' ? `/applications/${appId}` : `/applications/${appId}?panel=${pageId}`
);

const under = (pathname, root) => pathname === root || pathname.startsWith(`${root}/`);

// The three Applications sub-pages that read across every app sit under
// Usage, not Apps; everything else under /applications is the app list or
// one app.
const USAGE_PAGES = {
  '/applications/metrics': 'metrics',
  '/applications/os-evals': 'evals',
  '/applications/connections': 'connections',
};

function placeIds(pathname, search) {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (USAGE_PAGES[path]) return ['usage', USAGE_PAGES[path]];
  if (under(path, '/applications') || under(path, '/api-docs') || under(path, '/developer')) return ['apps', 'apps'];
  if (under(path, '/plugins')) return ['plugins', 'plugins'];
  if (under(path, '/schemas')) return ['schemas', 'schemas'];
  if (under(path, '/create')) {
    const lane = new URLSearchParams(search || '').get('lane');
    return ['develop', lane === 'continue' ? 'continue' : 'new'];
  }
  if (under(path, '/tokens')) return ['develop', 'tokens'];
  if (under(path, '/billing')) return ['money', 'money'];
  if (under(path, '/documentation')) return ['learn', 'docs'];
  if (under(path, '/cli')) return ['learn', 'cli'];
  if (under(path, '/releases')) return ['learn', 'releases'];
  return [null, null];
}

/** Inside one app? Its id and the page showing (an unknown `?panel=` is Overview), else null. */
export function appPlaceOf(pathname, search = '') {
  const m = /^\/applications\/([^/]+)\/?$/.exec(pathname || '');
  if (!m || USAGE_PAGES[`/applications/${m[1]}`] || m[1] === 'all') return null;
  const panel = new URLSearchParams(search || '').get('panel');
  const page = APP_PAGES.find((p) => p.id === panel) || APP_PAGES[0];
  return { appId: m[1], page };
}

/** Where a URL stands: its section and page, or nulls for Home and anything unmapped. */
export function placeOf(pathname, search = '') {
  const [sectionId, pageId] = placeIds(pathname || '/', search);
  const section = sectionFor(sectionId);
  const page = section ? section.pages.find((p) => p.id === pageId) || null : null;
  return { section, page };
}
